// Sorting comparators and helpers used to sort and display the game library.

/**
 * Sorts an array of strings alphabetically (case-insensitive).
 */
export function sortStringsAlpha(list) {
  return [...(list || [])].sort((a, b) =>
    String(a || "").localeCompare(String(b || ""), undefined, {
      sensitivity: "base",
    }),
  );
}

/**
 * Sorts an array of candidate objects alphabetically by their cleaned or raw title.
 */
export function sortCandidatesAlpha(list) {
  return [...(list || [])].sort((a, b) =>
    String(a.cleaned || a.raw || "").localeCompare(
      String(b.cleaned || b.raw || ""),
      undefined,
      { sensitivity: "base" },
    ),
  );
}

/**
 * Extracts a numeric Metacritic score from a game object.
 * Returns null if no valid score is found (unrated).
 */
export function getMetacriticNumber(game) {
  const v =
    game?.metacritic ?? game?.metacriticScore ?? game?.metaScore ?? null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Safely extracts a text string from a value that could be a string,
 * object, or number. Returns the fallback if nothing useful is found.
 */
export function safeText(v, fallback = "") {
  if (typeof v === "string") return v;
  if (v && typeof v === "object")
    return v.name || v.title || v.slug || fallback;
  if (typeof v === "number") return String(v);
  return fallback;
}

/**
 * Comparator for sorting games by title (A-Z or Z-A).
 */
export function compareByTitle(a, b, dir = "asc") {
  const ta = safeText(a?.title, "").trim();
  const tb = safeText(b?.title, "").trim();
  const cmp = ta.localeCompare(tb, undefined, { sensitivity: "base" });
  return dir === "asc" ? cmp : -cmp;
}

/**
 * Comparator for sorting games by Metacritic score.
 * Unrated games always sort to the bottom regardless of direction.
 * Ties are broken alphabetically by title.
 */
export function compareByMetacritic(a, b, dir = "desc") {
  const ma = getMetacriticNumber(a);
  const mb = getMetacriticNumber(b);

  const aMissing = ma === null;
  const bMissing = mb === null;
  if (aMissing && bMissing) return compareByTitle(a, b, "asc");
  if (aMissing) return 1;
  if (bMissing) return -1;

  const diff = ma - mb;
  if (diff === 0) return compareByTitle(a, b, "asc");
  return dir === "asc" ? diff : -diff;
}

/**
 * Extracts a numeric RAWG rating (0–5) from a game object.
 * Returns null if missing or stored as a metacritic-range value (> 5).
 */
export function getRawgRatingNumber(game) {
  const v = game?.rating ?? null;
  const n = Number(v);
  return Number.isFinite(n) && n <= 5 ? n : null;
}

/**
 * Comparator for sorting games by RAWG rating.
 * Unrated games always sort to the bottom regardless of direction.
 * Ties are broken alphabetically by title.
 */
export function compareByRawg(a, b, dir = "desc") {
  const ra = getRawgRatingNumber(a);
  const rb = getRawgRatingNumber(b);

  const aMissing = ra === null;
  const bMissing = rb === null;
  if (aMissing && bMissing) return compareByTitle(a, b, "asc");
  if (aMissing) return 1;
  if (bMissing) return -1;

  const diff = ra - rb;
  if (diff === 0) return compareByTitle(a, b, "asc");
  return dir === "asc" ? diff : -diff;
}

/**
 * Comparator for sorting games by addedAt date.
 * Games with no addedAt sort to the bottom. Ties broken alphabetically.
 */
export function compareByAddedAt(a, b, dir = "desc") {
  const da = a?.addedAt ? new Date(a.addedAt).getTime() : 0;
  const db = b?.addedAt ? new Date(b.addedAt).getTime() : 0;
  if (da === 0 && db === 0) return compareByTitle(a, b, "asc");
  if (da === 0) return 1;
  if (db === 0) return -1;
  const diff = da - db;
  return dir === "desc" ? -diff : diff;
}

/**
 * Returns the human-readable label for a given sort key.
 */
export function getSortLabel(sortValue) {
  switch (sortValue) {
    case "name_desc":
      return "Name (Z-A)";
    case "meta_desc":
      return "Metacritic (High-Low)";
    case "meta_asc":
      return "Metacritic (Low-High)";
    case "added_desc":
      return "Added (Newest)";
    case "added_asc":
      return "Added (Oldest)";
    case "name_asc":
    default:
      return "Name (A-Z)";
  }
}
